import React from 'react';
import { ControlPanelProps } from '../types';

const SliderControl: React.FC<{
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  disabled: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ id, label, value, min, max, step, unit, disabled, onChange }) => (
  <div className="flex flex-col space-y-2">
    <div className="flex justify-between items-baseline">
      <label htmlFor={id} className={`text-sm font-medium transition-colors ${disabled ? 'text-slate-500' : 'text-slate-300'}`}>{label}</label>
      <span className={`text-lg font-semibold transition-colors ${disabled ? 'text-slate-500' : 'text-cyan-400'}`}>
        {value} <span className={`text-sm ${disabled ? 'text-slate-600' : 'text-slate-400'}`}>{unit}</span>
      </span>
    </div>
    <input
      id={id}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={onChange}
      disabled={disabled}
      aria-valuetext={`${value} ${unit}`}
      className={`w-full h-2 bg-slate-700 rounded-lg appearance-none transition-opacity ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer accent-cyan-500'}`}
    />
  </div>
);

const ControlPanel: React.FC<ControlPanelProps> = ({
  rpm, setRpm, pressure, setPressure, openingTime, setOpeningTime, isRunning, toggleSimulation,
  isFlameViewActive, toggleFlameView, temperature, setTemperature,
  simulationSpeeds, simulationSpeed, setSimulationSpeed
}) => {
  return (
    <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700 shadow-lg w-full max-w-5xl mx-auto space-y-6">
      <div className="flex flex-wrap gap-3 justify-between items-center mb-4">
        <h3 className="text-xl font-bold text-white">Pannello di Controllo</h3>
        <div className="flex space-x-2">
          <button
            onClick={toggleFlameView}
            disabled={isRunning}
            className={`px-4 py-2 rounded-lg font-bold text-white text-sm transition-all duration-200 ease-in-out transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${isFlameViewActive ? 'bg-purple-600 hover:bg-purple-500' : 'bg-blue-600 hover:bg-blue-500'}`}
            aria-label={isFlameViewActive ? "Torna alla simulazione" : "Mostra solo la fiamma"}
          >
            {isFlameViewActive ? 'VISTA SIMULATORE' : 'MOSTRA FIAMMA'}
          </button>
          <button
            onClick={toggleSimulation}
            disabled={isFlameViewActive}
            className={`px-4 py-2 rounded-lg font-bold text-white text-sm transition-all duration-200 ease-in-out transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${isRunning ? 'bg-red-600 hover:bg-red-500' : 'bg-green-600 hover:bg-green-500'}`}
            aria-label={isRunning ? "Arresta simulazione" : "Avvia simulazione"}
          >
            {isRunning ? 'ARRESTA MOTORE' : 'AVVIA MOTORE'}
          </button>
        </div>
      </div>
      <SliderControl
        id="slider-rpm"
        label="Velocità Motore (RPM)"
        value={rpm}
        min={800}
        max={5000}
        step={100}
        unit="RPM"
        disabled={isFlameViewActive}
        onChange={(e) => setRpm(Number(e.target.value))}
      />
       <SliderControl
        id="slider-temperature"
        label="Temperatura"
        value={temperature}
        min={500}
        max={2000}
        step={50}
        unit="°C"
        disabled={false}
        onChange={(e) => setTemperature(Number(e.target.value))}
      />
      <SliderControl
        id="slider-pressure"
        label="Pressione di Lavoro"
        value={pressure}
        min={800}
        max={2500}
        step={50}
        unit="bar"
        disabled={false}
        onChange={(e) => setPressure(Number(e.target.value))}
      />
      <SliderControl
        id="slider-opening-time"
        label="Tempo di Apertura Iniettore"
        value={openingTime}
        min={0.5}
        max={5}
        step={0.1}
        unit="ms"
        disabled={isFlameViewActive}
        onChange={(e) => setOpeningTime(Number(e.target.value))}
      />
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-700 pt-4">
        <span className="text-sm font-medium text-slate-300">Velocità simulazione</span>
        <div className="flex space-x-1" role="group" aria-label="Velocità simulazione">
          {simulationSpeeds.map(speed => (
            <button
              key={speed.value}
              onClick={() => setSimulationSpeed(speed.value)}
              aria-pressed={simulationSpeed === speed.value}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                simulationSpeed === speed.value
                  ? 'bg-cyan-500 text-white shadow-md'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {speed.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ControlPanel;
